import { format } from "date-fns";
import { Field, Form, Formik } from "formik";
import _isEmpty from "lodash/isEmpty";
import _isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Button from "components/lib/Shared/Button";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "components/lib/Shared/Icons/sflIcons";
import ProfileLabel from "components/lib/Shared/ProfileLabel";
import SectionDescription from "components/lib/Shared/SectionDescription";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getDocumentData } from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm/documentData";
import { Constants } from "config/constants";
import { updateUser } from "state/slices/user";
import options from "../PLCTABS/SFLdata/selectOptions";
import { PlusIcon } from "components/lib/Shared/Icons/sflIcons";
import Label from "components/lib/Shared/Label";
import { getFormFields, getFullName } from "utils";
import ProfessionalDetail from "./ProfessionalDetail";
import {
  getProfessionalDetailsAsync,
  resetProfessionalDetailsSlice,
} from "state/slices/professionalDetails";
import { useToast } from "hooks/useToast";

const formFields = [
  "names",
  "identificationDetails",
  "postalAddress",
  "electronicAddress",
  "residentialAddressDetails",
  "fileUploads",
];

const defaultRoles = ["Company Secretary", "Advocate", "Auditor"];

function ConfirmDetails({ handleBackBtn }) {
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [accessType, setAccessType] = useState("");
  const history = useHistory();
  const { currentUser } = useSelector((state) => state.authSlice);
  const userSlice = useSelector((state) => state.userSlice);
  const profileUpdate = useSelector((state) => state.profileUpdateSlice);
  const accessRequests = useSelector(
    (state) => state.accessRequestsSlice.items
  );
  const [addNewDetail, setAddNewDetail] = useState(false);
  const professionalDetails = useSelector(
    (state) => state.professionalDetailsSlice.items
  );

  const fullName = getFullName(currentUser?.names);
  const { toast } = useToast(5000);
  const dispatch = useDispatch();

  const handleAccessType = (e) => setAccessType(e.target.value);

  const filteredAccessRequestForProfile = accessRequests.find(
    (accessRequest) =>
      accessRequest.initiatedToEmail &&
      !accessRequest?.isActive &&
      accessRequest?.status === "Pending" &&
      Constants.ACCESS_REQUEST.PERSONAL_DATA === accessRequest.requestFor
  );

  const filteredAccessRequestForCoApplicant = accessRequests.find(
    (accessRequest) =>
      accessRequest.initiatedToEmail &&
      !accessRequest?.isActive &&
      accessRequest?.status === "Pending" &&
      Constants.ACCESS_REQUEST.CO_APPLICANT === accessRequest.requestFor
  );

  const filteredAccessRequestForCorporate = accessRequests.find(
    (accessRequest) =>
      accessRequest.initiatedToEmail &&
      !accessRequest?.isActive &&
      accessRequest?.status === "Pending" &&
      accessRequest.requestFor === Constants.ACCESS_REQUEST.CORPORATE_DATA
  );

  const activeAccessRequestForProfileOrCoApplicant =
    filteredAccessRequestForProfile || filteredAccessRequestForCoApplicant;

  const {
    idType,
    isNonResident,
    names,
    identificationDetails,
    postalAddress,
    electronicAddress,
    residentialAddressDetails,
    fileUploads,
    isProfessional,
    attachProfessionsDetails,
    updateUserStatus,
    profileType,
  } = profileUpdate;

  useEffect(() => {
    setAccessType(activeAccessRequestForProfileOrCoApplicant?.accessType);
  }, [activeAccessRequestForProfileOrCoApplicant]);

  useEffect(() => {
    if (currentUser?._id) {
      dispatch(getProfessionalDetailsAsync({ user: currentUser?._id }));
    }
    return () => {
      dispatch(resetProfessionalDetailsSlice());
    };
  }, [currentUser?._id]);

  const handleSubmit = async () => {
    const newUserData = {};

    if (
      profileUpdate?.profileType &&
      currentUser?.profileType &&
      profileUpdate?.profileType !== currentUser?.profileType
    ) {
      newUserData.profileType = profileUpdate?.profileType;
    }

    formFields.map((formField) => {
      if (!_isEqual(profileUpdate[formField], currentUser[formField])) {
        newUserData[formField] = profileUpdate[formField];
      }
    });

    if (!_isEmpty(newUserData)) {
      try {
        await dispatch(
          updateUser({
            id: currentUser._id,
            data: {
              ...newUserData,
              idType: profileUpdate.idType,
              initiatedToEmail:
                activeAccessRequestForProfileOrCoApplicant?.initiatedToEmail,
            },
          })
        );
      } catch (error) { }
    }
    if (history.location.pathname === "/profile/edit") {
      history.push("/profile");
    }
    if (
      activeAccessRequestForProfileOrCoApplicant ||
      filteredAccessRequestForCorporate
    ) {
      history.push("/account-settings");
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{ fileUploads }}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => {
        const [isLoading, setIsLoading] = useState(false);
        const [selectedDetails, setSelectedDetail] = useState(null);
        const [addNewDetail, setAddNewDetail] = useState(false);

        const handleChange = async (addDetails) => {
          if (addDetails) {
            setFieldValue("attachProfessionsDetails", true);
          } else {
            dispatch(
              openModal({
                modalName: "deny_professional_data_confirmation_modal",
              })
            );
          }
        };

        const handleDenyProfessionalData = async () => {
          try {
            setIsLoading(true);
            await dispatch(
              updateUser({
                id: selectedProfile._id,
                data: {
                  isProfessional: false,
                  attachProfessionsDetails: false,
                },
              })
            );
            setFieldValue("attachProfessionsDetails", false);
            dispatch(closeModal("deny_professional_data_confirmation_modal"));
          } catch (error) {
            toast(
              "error",
              error?.message ?? "Something went wrong please try again."
            );
          } finally {
            setIsLoading(false);
          }
        };

        const handleAddNewProfessionalDetail = () => {
          setSelectedDetail(null);
          setAddNewDetail(true);
        };

        const handleEditProfessionalDetail = (professionalDetail) => {
          setSelectedDetail(professionalDetail);
          setAddNewDetail(false);
        };

        const assignedRoles = professionalDetails.map(
          ({ capacity }) => capacity
        );
        const capacities = defaultRoles.filter(
          (capacity) => !assignedRoles.includes(capacity)
        );
        return (
          <Form>
            <SectionDescription
              id="Step: 5 of 5"
              title="Confirm your details"
              description=""
            />
            <div className="px-2 space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
              <div className="px-4 my-4 sm:px-0">
                <div>
                  <div>
                    <div className="grid gap-6">
                      <div className="px-4 mt-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          Personal details
                        </h3>
                        <div className="flex flex-col space-y-2">
                          <ProfileLabel
                            label="Full names"
                            value={fullName || "N/A"}
                          />
                          {names?.formerName && (
                            <ProfileLabel
                              label="Former Name"
                              value={names?.formerName}
                            />
                          )}

                          {idType &&
                            Constants.FORM_FIELDS?.[
                              idType
                            ]?.identificationDetails?.map((formField) => {
                              let value =
                                identificationDetails?.[formField.key] ?? "N/A";
                              if (formField.isDate) {
                                if (value === "N/A") return;
                                value = format(new Date(value), "yyyy/MM/dd");
                              }

                              if (formField.isCountry && value) {
                                value =
                                  options.countries.find(
                                    (country) => country.value === value
                                  )?.label ?? "N/A";
                              }
                              /** Remove guardian details on minor profile */
                              return (
                                ![
                                  "Guardian Full Name",
                                  "Guardian Relationship",
                                  "Guardian Email Address",
                                  "Guardian Mobile Number",
                                ].includes(formField.label) &&
                                value !== "N/A" && (
                                  <ProfileLabel
                                    label={formField.label}
                                    value={value}
                                    key={formField.key}
                                  />
                                )
                              );
                            })}
                        </div>
                      </div>
                      <div>
                        {
                          <div className="px-4 mt-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                            <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">
                              Postal details
                            </h3>
                            <div className="flex flex-col space-y-2">
                              {idType === "Kenyan Citizen" && (
                                <ProfileLabel
                                  key="isNonResident"
                                  label="Non-Resident"
                                  value={isNonResident}
                                />
                              )}
                              {idType &&
                                getFormFields({
                                  idType:
                                    isNonResident && isNonResident === "Yes"
                                      ? "Foreigner"
                                      : idType,
                                  formType: "postalAddress",
                                  formValues: postalAddress,
                                })?.map((formField) => (
                                  <ProfileLabel
                                    key={formField.key}
                                    label={formField.name}
                                    value={formField.value}
                                  />
                                ))}
                            </div>
                          </div>
                        }
                        <div className="px-4 mt-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                          <h3 className="my-4 text-lg font-medium leading-6 text-gray-900">
                            Electronic address
                          </h3>
                          <div className="flex flex-col space-y-2">
                            <ProfileLabel
                              label="Email address"
                              value={electronicAddress?.emailAddress ?? "N/A"}
                            />
                            <ProfileLabel
                              label="Mobile number"
                              value={electronicAddress?.mobileNumber ?? "N/A"}
                            />
                          </div>
                        </div>
                        {
                          <div className="px-4 mt-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                            <h3 className="my-4 text-lg font-medium leading-6 text-gray-900">
                              Registered address
                            </h3>
                            <div className="flex flex-col space-y-2">
                              {idType &&
                                getFormFields({
                                  idType:
                                    isNonResident && isNonResident === "Yes"
                                      ? "Foreigner"
                                      : idType,
                                  formType: "residentialAddressDetails",
                                  formValues: residentialAddressDetails,
                                })?.map((formField) => (
                                  <ProfileLabel
                                    key={formField.name}
                                    label={formField.key}
                                    value={formField.value}
                                  />
                                ))}
                            </div>
                          </div>
                        }
                      </div>

                      {professionalDetails?.length > 0 && (
                        <div className="px-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                          <h3 className="my-4 text-lg font-medium leading-6 text-gray-900">
                            Professional qualifications
                          </h3>
                          {professionalDetails?.map(
                            (professionalDetail, professionalDetailIndex) => (
                              <div
                                key={`professionalDetail_${professionalDetail?._id}`}
                              >
                                <ProfessionalDetail
                                  professionalDetail={professionalDetail}
                                  isViewMode={
                                    selectedDetails?._id !==
                                    professionalDetail?._id
                                  }
                                  isEditMode={
                                    selectedDetails?._id ===
                                    professionalDetail?._id
                                  }
                                  handleEdit={handleEditProfessionalDetail}
                                />
                                {professionalDetailIndex <
                                  professionalDetails?.length - 1 && <hr />}
                              </div>
                            )
                          )}
                        </div>
                      )}

                      {!_isEmpty(fileUploads) && (
                        <div>
                          <div className="px-4 mt-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              File uploads
                            </h3>
                            <div className="flex flex-col space-y-2">
                              <Field
                                name="fileUploads"
                                component={FileUploadForm}
                                documentData={getDocumentData(idType)}
                                onlyPreview
                                showDocumentLabel
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between py-3">
              <Button
                disabled={isSubmitting}
                onClick={handleBackBtn}
                overrideBaseStyles
                className="flex items-center px-2 py-2 button button-back button-back-hover button-focus button-disabled button-outline"
              >
                <div className="mr-1.5 flex items-center">
                  <ChevronLeftIcon />
                  Back to previous step
                </div>
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                loadingText="Updating..."
                isLoading={isSubmitting}
              >
                <div className="ml-1.5 flex items-center">Update Profile</div>
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export default ConfirmDetails;
